import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { SectionEyebrow } from '../../../components/ui/SectionEyebrow';
import { Toggle } from '../../../components/ui/Toggle';
import { useAuth } from '../../../lib/auth/AuthContext';

export const ProfileScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, profileLoading, signOut, refreshProfile } = useAuth();
  const email = profile?.email ?? user?.email ?? '—';
  const subscriptionLabel =
    profile?.subscription_active === true ? 'Active subscription' : 'No active subscription';

  return (
    <div className="w-full max-w-5xl space-y-5 bg-background px-4 pb-10 pt-6 sm:px-6 lg:mx-auto lg:px-8 lg:pb-10 lg:pt-8">
      <header className="space-y-2">
        <h1 className="sr-only">Profile</h1>
        <SectionEyebrow>Profile</SectionEyebrow>
        <p className="text-sm leading-snug text-textSecondary">
          Gently adjust how Newrotune meets you. You can reset personalisation at any time.
        </p>
      </header>

      <Card tone="default" className="transition duration-300 hover:-translate-y-0.5">
        <div className="mb-4 space-y-2">
          <SectionEyebrow>You</SectionEyebrow>
        </div>
        <div className="rounded-2xl border border-borderSoft/40 bg-white/80 px-4 py-4 sm:px-5">
          <p className="text-sm font-medium leading-snug text-text">
            {profileLoading ? 'Loading account…' : `Signed in as ${email}`}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-textSecondary">
            Tier: {profile?.subscription_tier ?? 'free'} · {subscriptionLabel}
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" size="sm" fullWidth className="sm:flex-1" onClick={() => refreshProfile()}>
              Refresh status
            </Button>
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              className="sm:flex-1 text-textSecondary"
              onClick={async () => {
                await signOut();
                navigate('/auth', { replace: true });
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </Card>

      <Card tone="muted" className="transition duration-300 hover:-translate-y-0.5">
        <div className="mb-4 space-y-2">
          <SectionEyebrow>Notifications</SectionEyebrow>
          <p className="text-sm leading-snug text-textSecondary">
            We&apos;ll never rush or pressure you.
          </p>
        </div>
        <div className="space-y-3 rounded-2xl border border-borderSoft/40 bg-white/80 px-4 py-4 sm:px-5">
          <Toggle label="Gently remind me after good sessions" />
          <Toggle label="Let me know when new relevant content lands" />
        </div>
      </Card>

      <Card tone="default" className="transition duration-300 hover:-translate-y-0.5">
        <div className="mb-4 space-y-2">
          <SectionEyebrow>Personalisation</SectionEyebrow>
        </div>
        <div className="space-y-3 rounded-2xl border border-borderSoft/40 bg-white/80 px-4 py-4 sm:px-5">
          <p className="text-sm leading-relaxed text-textSecondary">
            Recommendations are based on your answers, recent choices, and gentle variety. You can
            clear this at any time.
          </p>
          <Button variant="outline" size="sm" fullWidth>
            Reset personalisation
          </Button>
        </div>
      </Card>

      <Card tone="default" className="transition duration-300 hover:-translate-y-0.5">
        <div className="mb-4 space-y-2">
          <SectionEyebrow>Account</SectionEyebrow>
        </div>
        <div className="space-y-3 rounded-2xl border border-borderSoft/40 bg-white/80 px-4 py-4 sm:px-5">
          {profile?.subscription_active === true ? (
            <p className="text-sm text-textSecondary">You have full access to audio and video on Explore.</p>
          ) : (
            <p className="text-sm text-textSecondary">Manage upgrades and payment details from the Billing page.</p>
          )}
          <Button variant="outline" size="sm" fullWidth onClick={() => navigate('/app/billing')}>
            Open billing
          </Button>
          <Button variant="ghost" size="sm" fullWidth className="text-danger">
            Delete account (coming later)
          </Button>
        </div>
      </Card>
    </div>
  );
};
